import React from 'react';
import { colors } from '@dhis2/ui';
import { ClipboardCheckIcon, MessageCircleIcon } from 'lucide-react';
import { GiLinkedRings, GiOnTarget, GiProgression, GiWeight } from 'react-icons/gi';
import { GoIssueDraft } from 'react-icons/go';

const ObjectiveViewModal = ({ objective, index, onClose, goalItems  }) => {
  if (!objective) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not specified';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Helper to extract weight from various possible locations
  const getObjectiveWeight = (obj) => {
    // Check direct weight property
    if (obj.weight && obj.weight > 0) return obj.weight;
    
    // Check in dataValues
    if (obj.dataValues) {
      const weightDV = obj.dataValues.find(
        dv => dv.dataElement === 'yfb9RCRpMB2' || // Weight data element
              dv.dataElement === 'lLCT66J73BQ'    // Alternative weight DE
      );
      if (weightDV?.value) {
        const parsed = parseInt(weightDV.value);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    }
    
    return 0;
  };

  const weight = getObjectiveWeight(objective);

  // Helper to extract KRAs from various possible locations
  const getKRAs = (obj) => {
    // If kras array exists and has items
    if (obj.kras && Array.isArray(obj.kras) && obj.kras.length > 0) {
      return obj.kras.filter(k => k?.name?.trim());
    }
    
    // Try to extract from dataValues if available
    if (obj.dataValues) {
      // This would need proper KRA extraction logic based on your data structure
      // You might need to fetch KRAs separately or they should be included in the objective
    }
    
    return [];
  };

  // Helper to extract KPIs from various possible locations
  const getKPIs = (obj) => {
    // If kpis array exists and has items
    if (obj.kpis && Array.isArray(obj.kpis) && obj.kpis.length > 0) {
      return obj.kpis.filter(k => k?.name?.trim());
    }
    
    // Try to extract from dataValues if available
    if (obj.dataValues) {
      // This would need proper KPI extraction logic based on your data structure
    }
    
    return [];
  };

  const kras = getKRAs(objective);
  const kpis = getKPIs(objective);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: colors.blue100
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: colors.blue600,
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.9rem'
              }}
            >
              {index + 1}
            </div>
            <h3 style={{ margin: 0, color: colors.blue900, fontSize: '1.1rem' }}>
              {objective.name || `Objective ${index + 1}`}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: colors.grey700,
              padding: '4px 8px',
              borderRadius: '4px'
            }}
            title="Close"
          >
            &times;
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          {/* Weight Badge */}
          {weight > 0 && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: colors.blue050,
                border: `1px solid ${colors.blue300}`,
                borderRadius: '20px',
                marginBottom: '16px',
                fontSize: '0.9rem',
                fontWeight: 500
              }}
            >
              <span style={{ color: colors.blue900 }}> <GiWeight size={18}/> Weight:</span>
              <strong style={{ color: colors.blue700 }}>{weight}%</strong>
            </div>
          )}
          
          {/* Description */}
          {objective.description && (
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontWeight: 600,
                  color: colors.grey800,
                  marginBottom: '6px',
                  fontSize: '0.95rem'
                }}
              >
                Description
              </label>
              <p
                style={{
                  margin: 0,
                  padding: '12px',
                  backgroundColor: colors.grey050,
                  borderRadius: '4px',
                  color: colors.grey900,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap'
                }}
              >
                {objective.description}
              </p>
            </div>
          )}

          {/* Dates */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              marginBottom: '20px'
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontWeight: 600,
                  color: colors.grey800,
                  marginBottom: '6px',
                  fontSize: '0.95rem'
                }}
              >
                Start Date
              </label>
              <p
                style={{
                  margin: 0,
                  padding: '10px 12px',
                  backgroundColor: colors.grey050,
                  borderRadius: '4px'
                }}
              >
                {formatDate(objective.startDate)}
              </p>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontWeight: 600,
                  color: colors.grey800,
                  marginBottom: '6px',
                  fontSize: '0.95rem'
                }}
              >
                Completion Date
              </label>
              <p
                style={{
                  margin: 0,
                  padding: '10px 12px',
                  backgroundColor: colors.grey050,
                  borderRadius: '4px'
                }}
              >
                {formatDate(objective.completionDate)}
              </p>
            </div>
          </div>

          {/* Alignment Badges */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {objective.objectiveAlignment && (
              <span
                style={{
                  padding: '4px 10px',
                  backgroundColor: colors.purple100,
                  color: colors.purple800,
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  fontWeight: 500
                }}
              >
                ⬆️ Aligned to Manager Objective
              </span>
            )}
            {objective.companyAlignedGoal && (
              <span
                style={{
                  padding: '4px 10px',
                  backgroundColor: colors.green100,
                  color: colors.green800,
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  fontWeight: 500
                }}
              >
                <GiLinkedRings size={14}/> Linked to Company Goal
              </span>
            )}
            {String(objective.status || '').toLowerCase() === 'draft' && (
              <span
                style={{
                  padding: '4px 10px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  fontWeight: 500
                }}
              >
                <GoIssueDraft size={14} /> Draft
              </span>
            )}
          </div>

          {/* KRAs Section */}
          {kras.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h4
                style={{
                  margin: '0 0 12px 0',
                  color: colors.grey800,
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span> <ClipboardCheckIcon size={18} /> </span> 
                Key Result Areas ({kras.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {kras.map((kra, kraIndex) => (
                  <div
                    key={kraIndex}
                    style={{
                      padding: '12px',
                      backgroundColor: colors.grey050,
                      border: `1px solid ${colors.grey300}`,
                      borderRadius: '4px'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '6px'
                      }}
                    >
                      <strong style={{ color: colors.grey900 }}>{kra.name}</strong>
                      {kra.weight > 0 && (
                        <span
                          style={{
                            fontSize: '0.85rem',
                            padding: '2px 8px',
                            backgroundColor: colors.blue100,
                            color: colors.blue800,
                            borderRadius: '10px',
                            fontWeight: 500
                          }}
                        >
                          {kra.weight}%
                        </span>
                      )}
                    </div>
                    {kra.description && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          color: colors.grey700,
                          lineHeight: 1.4
                        }}
                      >
                        {kra.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* KPIs Section */}
          {kpis.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h4
                style={{
                  margin: '0 0 12px 0',
                  color: colors.grey800,
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span> <GiProgression /></span> Key Performance Indicators ({kpis.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {kpis.map((kpi, kpiIndex) => (
                  <div
                    key={kpiIndex}
                    style={{
                      padding: '12px',
                      backgroundColor: colors.grey050,
                      border: `1px solid ${colors.grey300}`,
                      borderRadius: '4px'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '6px'
                      }}
                    >
                      <strong style={{ color: colors.grey900 }}>{kpi.name}</strong>
                      {kpi.target && (
                        <span
                          style={{
                            fontSize: '0.85rem',
                            padding: '2px 8px',
                            backgroundColor: colors.green100,
                            color: colors.green800,
                            borderRadius: '10px',
                            fontWeight: 500
                          }}
                        >
                         <GiOnTarget size={14}/> Target: {kpi.target}
                        </span>
                      )}
                    </div>
                    {kpi.description && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          color: colors.grey700,
                          lineHeight: 1.4
                        }}
                      >
                        {kpi.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Goal Information */}
              {objective.companyAlignedGoal && (
                <div style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#fff8e1',
                      border: '1px solid #ffecb3',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    
                    <div>
                      <p style={{ margin: '4px 0 0 0', color: '#856404' }}>
                        {objective.linkedGoalName || 
                        (goalItems?.find(g => g.id === objective.companyAlignedGoal)?.name) || 
                        'Unknown Goal'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {objective.objectiveAlignment && (
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#e8f4fd',
                    border: '1px solid #b3d9ff',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <span style={{ fontSize: '1.2rem' }}>⬆️</span>
                  <div>
                    <strong style={{ color: '#2c6693' }}>Aligned to Manager Objective:</strong>
                    <p style={{ margin: '4px 0 0 0', color: '#0b3d8a' }}>
                      {objective.higherLevelObjectiveName || objective.objectiveAlignment}
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* No KPIs/KRAs Message */}
          {kras.length === 0 && kpis.length === 0 && (
            <div
              style={{
                padding: '20px',
                backgroundColor: colors.grey050,
                borderRadius: '4px',
                textAlign: 'center',
                color: colors.grey600,
                marginBottom: '20px'
              }}
            >
              No KPIs or KRAs defined for this objective.
            </div>
          )}

          {/* Remarks Section */}
          {objective.remarks && (
            <div
              style={{
                padding: '12px',
                backgroundColor: colors.orange050,
                border: `1px solid ${colors.orange300}`,
                borderRadius: '4px'
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600,
                  color: colors.orange800,
                  marginBottom: '8px',
                  fontSize: '0.95rem'
                }}
              >
                <span> <MessageCircleIcon size={16} /> </span> Manager's Remarks
                {objective.approvalStatus === 'returned' && (
                  <span
                    style={{
                      fontSize: '0.75rem',
                      padding: '2px 6px',
                      backgroundColor: colors.orange200,
                      color: colors.orange900,
                      borderRadius: '8px',
                      fontWeight: 500
                    }}
                  >
                    ⚠️ Returned
                  </span>
                )}
              </label>
              <p
                style={{
                  margin: 0,
                  padding: '10px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  color: colors.grey900,
                  fontStyle: 'italic',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap'
                }}
              >
                {objective.remarks}
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e0e0e0',
            backgroundColor: colors.grey050,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px'
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: colors.blue600,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.9rem'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ObjectiveViewModal;